/**
 * @file App.jsx
 * @description application entry point
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Jul 13, 2023 18:53
 */
import { useRoutes } from 'react-router-dom';
import router from 'src/router/routes';

import { SnackbarProvider } from 'notistack';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import ThemeProvider from './theme/ThemeProvider';
import CssBaseline from "@mui/material/CssBaseline"

import AppInit from './components/AppInit';
import NotificationService from 'src/services/notifications/NotificationService';

// components that should occur only once in the app
import AnnotationViewDialog from 'src/content/AnnotationView/AnnotationViewDialog';
import AnnotationDistribChartDialog from 'src/content/GeneModel/edit/Disease/AnnotationDistribCharts/ChartDialog';
import SessionAnnotationsDialog from 'src/content/Document/Search/Sessions/SessionAnnotations/SessionAnnotationsDialog';
import SuspenseLoader from 'src/components/SuspenseLoader';

import JobRequestSnackbar from 'src/services/notifications/JobRequestSnackbar';
import JobCompleteSnackbar from 'src/services/notifications/JobCompleteSnackbar';
import JobErrorSnackbar from 'src/services/notifications/JobErrorSnackbar';

import ServerNotifications from 'src/services/ServerPubSub/ServerNotifications';

function App() {
    const routes = router();
    const content = useRoutes(routes);

    const auth = {
        isInitialized: true
    };

    return (
        <ThemeProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <SnackbarProvider
                    maxSnack={8}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right'
                    }}
                    Components={{
                        jobRequest: JobRequestSnackbar,
                        jobComplete: JobCompleteSnackbar,
                        jobError: JobErrorSnackbar
                    }}
                >
                    <CssBaseline />

                    <NotificationService>
                        {auth.isInitialized ? content : <AppInit />}
                        <ServerNotifications />
                        <AnnotationViewDialog />
                        <AnnotationDistribChartDialog />
                        <SessionAnnotationsDialog />
                        <SuspenseLoader />
                    </NotificationService>

                </SnackbarProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
}
export default App;